import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Badge,
  Chip,
  Box,
  Collapse,
  Divider,
  Button,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@material-ui/icons';
import NotificacionesService from '../../../services/NotificacionesService';

const useStyles = makeStyles((theme) => ({
  notificationCard: {
    marginBottom: theme.spacing(2),
    backgroundColor: '#ffffff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
  },
  notificationHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: theme.spacing(2),
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e0e0e0',
    borderRadius: '8px 8px 0 0',
  },
  notificationList: {
    maxHeight: 400,
    overflowY: 'auto',
  },
  notificationItem: {
    borderLeft: '4px solid transparent',
    marginBottom: theme.spacing(1),
    backgroundColor: 'white',
    borderRadius: theme.spacing(1),
    '&.warning': {
      borderLeftColor: '#ff9800',
    },
    '&.error': {
      borderLeftColor: '#f44336',
    },
    '&.info': {
      borderLeftColor: '#2196f3',
    },
    '&.success': {
      borderLeftColor: '#4caf50',
    },
  },
  notificationIcon: {
    minWidth: 40,
  },
  notificationContent: {
    flex: 1,
  },
  notificationTitle: {
    fontWeight: 'bold',
    fontSize: '0.875rem',
  },
  notificationMessage: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  notificationTime: {
    fontSize: '0.7rem',
    color: theme.palette.text.disabled,
  },
  priorityChip: {
    height: 20,
    fontSize: '0.7rem',
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: theme.palette.text.secondary,
  },
  refreshButton: {
    marginLeft: theme.spacing(1),
  },
  statsContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  statChip: {
    fontSize: '0.75rem',
  },
}));

const NotificacionesPanel = ({ data, onRefresh }) => {
  const classes = useStyles();
  
  const [expanded, setExpanded] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    generateNotifications();
  }, [data]);

  const generateNotifications = () => {
    if (!data || data.length === 0) {
      setNotifications([]);
      setStats({ total: 0, pendientes: 0, rechazadas: 0, criticas: 0, noLeidas: 0 });
      return;
    }

    const notificacionesPendientes = [];
    let pendientesCount = 0;
    let rechazadasCount = 0;
    let criticasCount = 0;

    data.forEach((item, index) => {
      const estado = item.estado_solicitud;
      const lugares = item.lugares_solicitados || 0;
      const universidad = item.universidad || 'Universidad';
      const carrera = item.carrera || 'Carrera';
      const servicio = item.servicio || 'Servicio';

      // NOTIFICACIONES PARA SOLICITUDES PENDIENTES
      if (estado === 'Pendiente') {
        pendientesCount++;
        notificacionesPendientes.push({
          id: `pendiente-${index}`,
          tipo: 'warning',
          prioridad: lugares > 10 ? 'alta' : 'media',
          titulo: `Solicitud Pendiente - ${lugares} plazas`,
          mensaje: `${universidad} - ${carrera} en ${servicio}`,
          fecha: new Date(),
          universidad,
          carrera,
          servicio,
          lugares_solicitados: lugares,
          estado,
          leida: false
        });
      }

      // NOTIFICACIONES PARA SOLICITUDES RECHAZADAS
      if (estado === 'Rechazado por DNERHS') {
        rechazadasCount++;
        notificacionesPendientes.push({
          id: `rechazada-${index}`,
          tipo: 'error',
          prioridad: 'alta',
          titulo: `Solicitud Rechazada - ${lugares} plazas`,
          mensaje: `${universidad} - ${carrera} en ${servicio}`,
          fecha: new Date(),
          universidad,
          carrera,
          servicio,
          lugares_solicitados: lugares,
          estado,
          leida: false
        });
      }

      // NOTIFICACIONES CRÍTICAS (muchas plazas disponibles sin usar)
      const disponibles = Math.max(0, (item.cantidad_plazas || 0) - (item.ocupadas_reales || 0) - (item.solicitadas_reales || 0) - (item.aprobadas_reales || 0));
      if (disponibles > 50) {
        criticasCount++;
        notificacionesPendientes.push({
          id: `critica-${index}`,
          tipo: 'info',
          prioridad: 'media',
          titulo: `${disponibles} plazas disponibles sin usar`,
          mensaje: `${universidad} - ${carrera} en ${servicio}`,
          fecha: new Date(),
          universidad,
          carrera,
          servicio,
          lugares_disponibles: disponibles,
          estado: 'Disponible',
          leida: false
        });
      }
    });

    // Ordenar por prioridad y fecha
    const sortedNotifications = notificacionesPendientes.sort((a, b) => {
      const prioridadOrder = { 'alta': 3, 'media': 2, 'baja': 1 };
      return prioridadOrder[b.prioridad] - prioridadOrder[a.prioridad];
    });

    setNotifications(sortedNotifications);
    setStats({
      total: sortedNotifications.length,
      pendientes: pendientesCount,
      rechazadas: rechazadasCount,
      criticas: criticasCount,
      noLeidas: sortedNotifications.length
    });
  };

  const handleDismissNotification = (notificationId) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
  };

  const getNotificationIcon = (tipo) => {
    switch (tipo) {
      case 'warning':
        return <WarningIcon style={{ color: '#ff9800' }} />;
      case 'error':
        return <ErrorIcon style={{ color: '#f44336' }} />;
      case 'info':
        return <InfoIcon style={{ color: '#2196f3' }} />;
      case 'success':
        return <SuccessIcon style={{ color: '#4caf50' }} />;
      default:
        return <NotificationsIcon />;
    }
  };

  const getPriorityColor = (prioridad) => {
    switch (prioridad) {
      case 'alta':
        return '#f44336';
      case 'media':
        return '#ff9800';
      case 'baja':
        return '#4caf50';
      default:
        return '#757575';
    }
  };

  const renderStats = () => (
    <div className={classes.statsContainer}>
      <Chip
        label={`Total Alertas: ${stats.total}`}
        size="small"
        className={classes.statChip}
        style={{ backgroundColor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }}
      />
      {stats.pendientes > 0 && (
        <Chip
          label={`⏳ Pendientes: ${stats.pendientes}`}
          size="small"
          className={classes.statChip}
          style={{ backgroundColor: '#fff8e1', color: '#f9a825', fontWeight: 600 }}
        />
      )}
      {stats.rechazadas > 0 && (
        <Chip
          label={`❌ Rechazadas: ${stats.rechazadas}`}
          size="small"
          className={classes.statChip}
          style={{ backgroundColor: '#ffebee', color: '#d32f2f', fontWeight: 600 }}
        />
      )}
      {stats.criticas > 0 && (
        <Chip
          label={`💡 Oportunidades: ${stats.criticas}`}
          size="small"
          className={classes.statChip}
          style={{ backgroundColor: '#e8f4fd', color: '#1976d2', fontWeight: 600 }}
        />
      )}
    </div>
  );

  const renderNotificationItem = (notification) => (
    <ListItem
      key={notification.id}
      className={`${classes.notificationItem} ${notification.tipo}`}
      style={{
        backgroundColor: 'white',
        borderRadius: 8,
        marginBottom: 12,
        border: '1px solid #e0e0e0',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
      }}
    >
      <ListItemIcon className={classes.notificationIcon}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            backgroundColor:
              notification.tipo === 'warning' ? '#fff3e0' :
              notification.tipo === 'error' ? '#ffebee' :
              notification.tipo === 'info' ? '#e3f2fd' :
              '#e8f5e8'
          }}
        >
          {getNotificationIcon(notification.tipo)}
        </Box>
      </ListItemIcon>

      <ListItemText
        className={classes.notificationContent}
        primary={
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <span style={{
              fontWeight: 700,
              fontSize: '0.95rem',
              color: '#333'
            }}>
              {notification.titulo}
            </span>
            <Chip
              label={notification.prioridad.toUpperCase()}
              size="small"
              style={{
                backgroundColor: getPriorityColor(notification.prioridad),
                color: 'white',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 24
              }}
            />
          </Box>
        }
        secondary={
          <Box>
            <Typography style={{
              fontSize: '0.85rem',
              color: '#555',
              marginBottom: 8,
              lineHeight: 1.4
            }}>
              {notification.mensaje}
            </Typography>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <span style={{
                fontSize: '0.75rem',
                color: '#999',
                fontStyle: 'italic'
              }}>
                Estado: {notification.estado}
              </span>
              <Chip
                label={`${notification.lugares_solicitados || notification.lugares_disponibles || 0} plazas`}
                size="small"
                style={{
                  backgroundColor: '#f5f5f5',
                  color: '#666',
                  fontSize: '0.7rem',
                  height: 20
                }}
              />
            </Box>
          </Box>
        }
      />

      <ListItemSecondaryAction>
        <Tooltip title="Descartar notificación">
          <IconButton
            size="small"
            onClick={() => handleDismissNotification(notification.id)}
            style={{
              backgroundColor: '#f5f5f5',
              '&:hover': {
                backgroundColor: '#e0e0e0'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </ListItemSecondaryAction>
    </ListItem>
  );

  return (
    <Card className={classes.notificationCard}>
      <div className={classes.notificationHeader} onClick={() => setExpanded(!expanded)}>
        <Box display="flex" alignItems="center">
          <Badge
            badgeContent={stats.noLeidas}
            color="error"
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <NotificationsIcon style={{ color: '#1976d2', fontSize: 28 }} />
          </Badge>
          <Box ml={2}>
            <Typography variant="h6" style={{
              fontWeight: 600,
              color: '#333',
              fontSize: '1.1rem',
              lineHeight: 1.2
            }}>
              🔔 Alertas de Solicitudes
            </Typography>
            <Typography variant="caption" style={{
              color: '#666',
              fontSize: '0.8rem'
            }}>
              Gestión de plazas pendientes
            </Typography>
          </Box>
          {stats.total > 0 && (
            <Chip
              label={`${stats.total} alertas`}
              size="small"
              style={{
                marginLeft: 16,
                backgroundColor: stats.pendientes > 0 || stats.rechazadas > 0 ? '#ffebee' : '#e8f5e8',
                color: stats.pendientes > 0 || stats.rechazadas > 0 ? '#d32f2f' : '#388e3c',
                fontWeight: 600,
                border: '1px solid #ddd'
              }}
            />
          )}
        </Box>
        <Box display="flex" alignItems="center">
          <Tooltip title="Actualizar notificaciones">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                generateNotifications();
                if (onRefresh) onRefresh();
              }}
              className={classes.refreshButton}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </div>

      <Collapse in={expanded}>
        <CardContent>
          {stats.total > 0 && renderStats()}
          
          {notifications.length > 0 ? (
            <List className={classes.notificationList}>
              {notifications.map((notification, index) => (
                <React.Fragment key={notification.id}>
                  {renderNotificationItem(notification)}
                  {index < notifications.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box className={classes.emptyState} textAlign="center" py={4}>
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: '50%',
                  backgroundColor: '#e8f5e8',
                  margin: '0 auto 16px auto'
                }}
              >
                <SuccessIcon style={{ fontSize: 40, color: '#388e3c' }} />
              </Box>
              <Typography variant="h6" style={{
                fontWeight: 600,
                color: '#388e3c',
                marginBottom: 8
              }}>
                ✅ Todo en orden
              </Typography>
              <Typography variant="body1" style={{ color: '#666', marginBottom: 4 }}>
                No hay solicitudes pendientes de atención
              </Typography>
              <Typography variant="body2" style={{ color: '#999' }}>
                Las alertas aparecerán automáticamente cuando haya solicitudes que requieran gestión
              </Typography>
            </Box>
          )}
          
          {notifications.length > 0 && (
            <Box mt={2} textAlign="center">
              <Button
                size="small"
                onClick={() => setNotifications([])}
              >
                Limpiar Todas
              </Button>
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default NotificacionesPanel;
